import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { AuthShell } from './AuthShell'
import { Alert, Button, TextField } from '@/components/layout/ui'
import { authErrorMessage, authLinkClass, emailProblem, FormError } from '@/features/auth'
import { sessionApi } from '@/lib/api'
import { fieldErrorFor } from '@/lib/errors'

/**
 * Request a reset link.
 *
 * The API answers identically whether or not the address exists, so the UI must
 * not imply otherwise — the confirmation is written to match ("if that address
 * is registered"), or the endpoint's careful non-disclosure would be undone by
 * the copy in front of it. That is also why the address is validated in the
 * browser: a shape check leaks nothing, and a round trip that could distinguish
 * two outcomes is exactly what this screen must not have.
 *
 * The sign-in form links here with `?email=`, so arriving from a mistyped
 * password does not mean typing the address again.
 */
export default function ForgotPasswordPage() {
  const [params] = useSearchParams()
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [emailError, setEmailError] = useState<string | undefined>(undefined)

  const request = useMutation({
    mutationFn: (address: string) => sessionApi.forgotPassword(address),
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const problem = emailProblem(email)
    setEmailError(problem)
    if (problem) return
    request.mutate(email.trim())
  }

  return (
    <AuthShell
      title="Reset your password"
      description="We will email you a link to choose a new one."
      footer={
        <Link to="/login" className={authLinkClass}>
          Back to log in
        </Link>
      }
    >
      {request.isSuccess ? (
        <Alert tone="success" title="Check your email">
          {request.data.message}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            autoFocus
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              if (emailError) setEmailError(undefined)
            }}
            // A client-side shape problem first, then whatever the API said
            // about the field — otherwise a 422 on `email` would be swallowed by
            // authErrorMessage, which stays quiet when a field can speak for itself.
            error={emailError ?? fieldErrorFor(request.error, 'email')}
          />

          <FormError message={authErrorMessage(request.error, ['email'], 'reset requests')} />

          <Button type="submit" variant="primary" className="w-full" loading={request.isPending}>
            {request.isPending ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
